import classNames from 'classnames';
import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';

import { FormControlViewModelType } from '../../../services/form/view-model/FormControlViewModel';

import FormButtonControl from './FormButtonControl';
import FormCheckboxControl from './FormCheckboxControl';
import FormCheckGroupControl from './FormCheckGroupControl';
import FormColorInputControl from './FormColorInputControl';
import { useFormContext } from './FormContext';
import { TFormControlConnectorProps } from './FormControlContainer';
import FormDatasetInputControl from './FormDatasetInputControl';
import FormDateInputControl from './FormDateInputControl';
import FormDictButtonControl from './FormDictButtonControl';
import FormDictDateInputControl from './FormDictDateInputControl';
import FormDictionaryInputControl from './FormDictInputControl';
import FormDictMultilineTextInputControl from './FormDictMultilineTextInputControl';
import FormFileInputControl from './FormFileInputControl';
import FormGroupControl from './FormGroupControl';
import FormImageInputControl from './FormImageInputControl';
import FormLabelControl from './FormLabelControl';
import FormLabeledInputControl from './FormLabeledInputControl';
import FormMonthYearControl from './FormMonthYearControl';
import FormMultilineTextInputControl from './FormMultilineTextInputControl';
import FormNumericInputControl from './FormNumericInputControl';
import FormReportControl from './FormReportControl';
import FormRichTextInputControl from './FormRichTextInputControl';
import FormSelectInputControl from './FormSelectInputControl';
import FormSignatureButtonControlContainer from './FormSignatureButtonControlContainer';
import FormTabPaneControl from './FormTabPaneControl';
import FormTabsControlContainer from './FormTabsControlContainer';
import FormTextInputControl from './FormTextInputControl';
import FormTimeInputControl from './FormTimeInputControl';
import FormTreeControl from './FormTreeControl';
import FormTreeNodeInputControl from './FormTreeNodeInputControl';
import { InputOnValidate } from './useForm';

const useStyles = createUseStyles({
  tabsPaneFullHeight: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  boxFullHeight: {
    flex: '1 1 0%',
    minHeight: 0,
  },
});

const controls = {
  [FormControlViewModelType.BOX]: FormGroupControl,
  [FormControlViewModelType.BUTTON]: FormButtonControl,
  [FormControlViewModelType.CHECKBOX_GROUP]: FormCheckGroupControl,
  [FormControlViewModelType.CHECKBOX]: FormCheckboxControl,
  [FormControlViewModelType.COLOR_INPUT]: FormColorInputControl,
  [FormControlViewModelType.DATASET]: FormDatasetInputControl,
  [FormControlViewModelType.DATE]: FormDateInputControl,
  [FormControlViewModelType.DICT_BUTTON]: FormDictButtonControl,
  [FormControlViewModelType.DICT_DATE]: FormDictDateInputControl,
  [FormControlViewModelType.DICT_MULTILINE]: FormDictMultilineTextInputControl,
  [FormControlViewModelType.DICTIONARY_INPUT]: FormDictionaryInputControl,
  [FormControlViewModelType.FILE]: FormFileInputControl,
  [FormControlViewModelType.FORM_BUTTON]: FormButtonControl,
  [FormControlViewModelType.IMAGE]: FormImageInputControl,
  [FormControlViewModelType.LABEL]: FormLabelControl,
  [FormControlViewModelType.LABELED]: FormLabeledInputControl,
  [FormControlViewModelType.MONTH_YEAR]: FormMonthYearControl,
  [FormControlViewModelType.MULTILINE]: FormMultilineTextInputControl,
  [FormControlViewModelType.NUMERIC_INPUT]: FormNumericInputControl,
  [FormControlViewModelType.REPORT]: FormReportControl,
  [FormControlViewModelType.RICHTEXT]: FormRichTextInputControl,
  [FormControlViewModelType.SELECT_INPUT]: FormSelectInputControl,
  [FormControlViewModelType.SIGNATURE_BUTTON]:
    FormSignatureButtonControlContainer,
  [FormControlViewModelType.TABS_PANE]: FormTabPaneControl,
  [FormControlViewModelType.TABS]: FormTabsControlContainer,
  [FormControlViewModelType.TEXT_INPUT]: FormTextInputControl,
  [FormControlViewModelType.TIME_INPUT]: FormTimeInputControl,
  [FormControlViewModelType.TREE_INPUT]: FormTreeNodeInputControl,
  [FormControlViewModelType.TREE]: FormTreeControl,
} as const;

const FormControl: React.FC<TFormControlConnectorProps> = ({
  areHintsShown,
  control,
  onChange,
  onChanged,
  formId,
  onRunSingleUpdater,
  disabled,
  onSubmit,
  warning,
}) => {
  const { errors, onValidate } = useFormContext();
  const classes = useStyles();

  const handleValidate = useCallback<InputOnValidate>(
    (validationErrors, name, isDirty) =>
      onValidate(validationErrors, name, isDirty, {
        minLength: 'minLength' in control ? control.minLength : undefined,
        maxLength: 'maxLength' in control ? control.maxLength : undefined,
        currentLength:
          control.hasValue && typeof control.value === 'string'
            ? control.value.length
            : undefined,
        min: 'min' in control ? control.min : undefined,
        max: 'max' in control ? control.max : undefined,
      }),
    [control, onValidate]
  );

  if (!control || control.visible === false) {
    return null;
  }

  const Control = controls[control.type];

  if (!Control) {
    throw new Error(`unsupported control type: ${control.type}`);
  }

  const ControlWithProps = (
    <Control
      areHintsShown={areHintsShown}
      disabled={disabled}
      formId={formId}
      control={{
        ...control,
        disabled: control.disabled || disabled,
      }}
      warning={warning}
      onChange={onChange}
      onChanged={onChanged}
      onRunSingleUpdater={onRunSingleUpdater}
      onSubmit={(arg: any) => errors.size === 0 && onSubmit(arg)}
      onValidate={handleValidate}
    />
  );

  const isFullHeightGroup =
    'fullHeightGroup' in control && control.fullHeightGroup;

  return 'rootControl' in control &&
    control.rootControl &&
    control.rootControl === control.name ? (
    ControlWithProps
  ) : (
    <div
      className={classNames(`col-${control.columns || 12}`, {
        row:
          control.type === FormControlViewModelType.TABS_PANE &&
          !isFullHeightGroup,
        'full-height':
          isFullHeightGroup && control.type !== FormControlViewModelType.BOX,
        [classes.tabsPaneFullHeight]:
          control.type === FormControlViewModelType.TABS_PANE &&
          isFullHeightGroup,
        [classes.boxFullHeight]:
          control.type === FormControlViewModelType.BOX && isFullHeightGroup,
      })}
    >
      {ControlWithProps}
    </div>
  );
};

export default FormControl;
